import { createAsyncThunk } from '@reduxjs/toolkit';
import get_saved_scenario from 'functions/server/get_saved_scenario.js';
import get_example_scenario from 'functions/server/get_example_scenario.js';
import delete_saved_scenario from 'functions/server/delete_saved_scenario.js';
import saveInstance from 'functions/server/save_instance.js';
import renameInstance from 'functions/server/rename_instance.js';
import renameInstanceName from 'functions/server/rename_instance_name.js';
import list_examples from 'functions/server/list_examples.js';
import {
  isExampleLoad,
  savesAsNewBecauseOfUpdate,
  scenarioIdentityOf,
  scenarioIdForSave,
  withScenarioIdentity,
} from 'store/functions/scenarioIdentity.js';

export const fetchExampleList = createAsyncThunk('posts/fetchExampleList', async () => {
  /*
  Fetches the list of published example scenarios (cards) from the backend.
  Takes no arguments; returns the array of example rows (may be empty).
  */
  const response = await list_examples();
  return Array.isArray(response) ? response : [];
});

export const fetchSavedScenario = createAsyncThunk('posts/fetchSavedScenario', async (payload) => {
  /*
  Fetches a saved scenario from the server. The payload should contain either a scenarioId or a scenarioName. 
  If a scenarioId is provided, the scenarioName is not needed.
  */
  const scenarioId = payload.scenarioId;
  const scenarioName = payload.scenarioName;
  // Example mode follows what is being loaded, not the mode the app was left in:
  // opening your own scenario after viewing an example is not an example.
  // See isExampleLoad in store/functions/scenarioIdentity.js.
  const exampleMode = isExampleLoad(payload) && !payload.modelMode;

  // An exampleSlug is sufficient on its own now that examples load by slug;
  // id/name are still required for a user's own saved scenario.
  if (!scenarioId && !scenarioName && !payload.exampleSlug) {
    throw new Error("Missing scenarioId, scenarioName or exampleSlug input to fetchSavedScenario.")
  }
  // A published example goes through the cacheable GET twin: URL-keyed, cached
  // at the edge, and structurally incapable of returning private data. The POST
  // /saved_scenario path could never be cached (CloudFront caches GET/HEAD), so
  // every gallery card open hit origin. Falls back to the POST path if the GET
  // fails, so a gateway/tier problem degrades to the old behaviour rather than
  // breaking the gallery.
  let response;
  if (payload.exampleSlug) {
    try {
      response = await get_example_scenario(payload.exampleSlug);
    } catch (e) {
      response = await get_saved_scenario(scenarioId, scenarioName);
    }
  } else {
    response = await get_saved_scenario(scenarioId, scenarioName);
  }
  // exampleSlug travels with an example-gallery load so solves on this scenario
  // can claim the example's entitlement allowance; null for every other load.
  return {'response': response, 'scenarioId': scenarioId, 'scenarioName': scenarioName, exampleMode: exampleMode, fromScratch: payload.fromScratch, exampleSlug: payload.exampleSlug ?? null}
});

export const deleteSavedScenario = createAsyncThunk('posts/deleteSavedScenario', async (payload) => {
  const scenario_id = payload.scenario_id;

  if (!scenario_id) {
    throw new Error("Missing scenario_id input to deleteSavedScenario.")
  }
  const response = await delete_saved_scenario(scenario_id)
  return response
});

export const saveScenario = createAsyncThunk('posts/saveScenario', async (payload, {getState}) => {
  /*
  Saves the current scenario to the server. The payload should contain a scenarioName.

  Args: 
    payload: Object with the following
      scenarioName: String, the name of the scenario to save
  */
  const scenarioName = payload.scenarioName;
  const state = getState();
  const userId = state.inputData.present.loginData.userId;
  const overwrite = payload.overwrite || false;
  // Which open scenario this save is for. The response is only applied to that
  // scenario, in case another was opened while the request was out.
  const identityBefore = scenarioIdentityOf(state.inputData.present);

  if (!scenarioName) {
    throw new Error("Missing scenarioName input to save scenario.")
  }
  // Overwrite the open scenario's own id; anything else is a new scenario. The
  // body is stamped with the id and name it is stored under, so a later load
  // cannot pick up a previous save's id. See store/functions/scenarioIdentity.js.
  const scenarioId = scenarioIdForSave(state.inputData.present, overwrite);
  const body = withScenarioIdentity(state.inputData.present, scenarioId, scenarioName);

  // A plain Save that becomes a new copy only because a site update forgot the
  // open scenario's id says so, once per browser (ui.rememberedSwitches).
  // `viaRename`: RenameScenarioButton saves a new copy when there is no id to rename.
  const savedAsNewAfterUpdate = savesAsNewBecauseOfUpdate(state.inputData.present, overwrite || Boolean(payload.viaRename))
    && !state.ui?.rememberedSwitches?.savedAsNewAfterUpdateNoticeShown;

  const response = await saveInstance(body, scenarioName, userId, scenarioId, overwrite)
  return {'response': response, 'scenarioId': scenarioId, scenarioName: scenarioName, savedAsNewAfterUpdate, identityBefore}
});

export const renameScenario = createAsyncThunk('posts/renameScenario', async (payload, {getState}) => {
  const scenarioId = payload.scenarioId;
  const newName = payload.newName;
  const state = getState()
  if (!scenarioId) {
    throw new Error("Missing scenarioId input to rename scenario.")
  }

  /* /update_instance replaces the stored body as well as the name, so the body
     sent must be THIS scenario's. Renaming the open scenario sends the open state;
     renaming another one (the saved-scenarios drawer lists them all) used to send
     the open state too, which replaced that other scenario's content with this
     one's. It now sends that scenario's own stored body. Either way the body is
     stamped with the id and new name, so a later load shows the new name. */
  const present = state.inputData.present;
  const renamedOpenScenario = present.generalSettings?.scenario?.scenarioId === scenarioId;
  const identityBefore = scenarioIdentityOf(present);
  const source = renamedOpenScenario ? present : await get_saved_scenario(scenarioId);

  let response;
  if (source && typeof source === 'object' && source.data && typeof source.data === 'object') {
    const body = withScenarioIdentity(source, scenarioId, newName);
    response = await renameInstance(body, scenarioId, newName);
  } else {
    // No usable stored body to send back (an empty or unexpected response).
    // Posting it to /update_instance would replace the scenario with it, so rename
    // the name alone instead.
    response = await renameInstanceName(scenarioId, newName);
  }

  return {response: response, scenarioName: newName, scenarioId, renamedOpenScenario, identityBefore}
});
